import type { FrontendToolSchema } from "@/stores"

export type EmbeddedChatModalSize = "compact" | "expanded"

// widget → host: the widget is listening, so anything the host queued can be delivered.
export type EmbeddedChatWidgetReadyMessage = {
  type: "WIDGET_READY"
}

// widget → host: the modal opened, grow the iframe to the compact size.
export type EmbeddedChatModalOpenMessage = {
  type: "MODAL_OPEN"
}

// widget → host: the modal closed, shrink the iframe back to the launcher.
export type EmbeddedChatModalCloseMessage = {
  type: "MODAL_CLOSE"
}

// widget → host: the size of an open modal was toggled.
export type EmbeddedChatModalResizeMessage = {
  type: "MODAL_RESIZE"
  value: EmbeddedChatModalSize
}

// widget → host: an assistant reply finished streaming, so the host page can react to what it
// says — e.g. expand the window when the reply renders a product carousel.
export type EmbeddedChatAssistantMessage = {
  type: "ASSISTANT_MESSAGE"
  value: { id: string; text: string }
}

// host → widget: window.MitigateEmbeddedChat.open()/close()/toggle().
export type EmbeddedChatSetModalOpenMessage = {
  type: "SET_MODAL_OPEN"
  value: boolean | "toggle"
}

// host → widget: window.MitigateEmbeddedChat.resize().
export type EmbeddedChatSetModalSizeMessage = {
  type: "SET_MODAL_SIZE"
  value: EmbeddedChatModalSize
}

// host → widget: window.MitigateEmbeddedChat.reset() — start a new conversation.
export type EmbeddedChatResetChatMessage = {
  type: "RESET_CHAT"
}

// host → widget: window.MitigateEmbeddedChat.loadChat() — switch to the host-supplied conversation.
export type EmbeddedChatLoadChatMessage = {
  type: "LOAD_CHAT"
  value: { chatId: string }
}

// host → widget: the host page switched between light and dark.
export type EmbeddedChatThemeMessage = {
  type: "THEME"
  value: { theme: "light" | "dark"; hostBg: string | null }
}

// host → widget: the tools the host registered via window.MitigateEmbeddedChat.registerTool().
export type EmbeddedChatRegisterToolsMessage = {
  type: "REGISTER_TOOLS"
  value: FrontendToolSchema[]
}

// widget → host: run a host-registered tool.
export type EmbeddedChatExecuteToolMessage = {
  type: "EXECUTE_TOOL"
  value: { requestId: string; name: string; args: Record<string, unknown> }
}

// host → widget: the result of an EXECUTE_TOOL call.
export type EmbeddedChatToolResultMessage = {
  type: "TOOL_RESULT"
  value: { requestId: string; result: unknown; isError?: boolean }
}

// widget → host: the TOOL_RESULT reached the runtime, so a host callback awaiting it can continue.
export type EmbeddedChatToolResultAckMessage = {
  type: "TOOL_RESULT_ACK"
  value: { requestId: string }
}

// host → widget: whether window.MitigateEmbeddedChat.onNavigate() has a callback registered. Links
// keep opening in a new tab on their own until the widget knows the host wants them.
export type EmbeddedChatSetNavigateHandlerMessage = {
  type: "SET_NAVIGATE_HANDLER"
  value: boolean
}

// widget → host: a link in the conversation was clicked and the host's onNavigate callback decides
// what happens to it. `url` is passed on exactly as the assistant wrote it, relative urls included,
// so the host resolves them against its own page rather than against the widget's origin.
export type EmbeddedChatNavigateMessage = {
  type: "NAVIGATE"
  value: { requestId: string; url: string }
}

// host → widget: what onNavigate() returned. `handled: false` hands the link back to the widget,
// which opens it in a new tab as it would have without a callback.
export type EmbeddedChatNavigateResultMessage = {
  type: "NAVIGATE_RESULT"
  value: { requestId: string; handled: boolean }
}

// Messages exchanged between the embedded chat widget (this app, inside the iframe) and the host
// page (app/views/api/v1/embedded_chat/show.js.erb, which owns the iframe and resizes it).
export type EmbeddedChatMessage =
  | EmbeddedChatWidgetReadyMessage
  | EmbeddedChatModalOpenMessage
  | EmbeddedChatModalCloseMessage
  | EmbeddedChatModalResizeMessage
  | EmbeddedChatAssistantMessage
  | EmbeddedChatSetModalOpenMessage
  | EmbeddedChatSetModalSizeMessage
  | EmbeddedChatResetChatMessage
  | EmbeddedChatLoadChatMessage
  | EmbeddedChatThemeMessage
  | EmbeddedChatRegisterToolsMessage
  | EmbeddedChatExecuteToolMessage
  | EmbeddedChatToolResultMessage
  | EmbeddedChatToolResultAckMessage
  | EmbeddedChatSetNavigateHandlerMessage
  | EmbeddedChatNavigateMessage
  | EmbeddedChatNavigateResultMessage

export type EmbeddedChatMessageType = EmbeddedChatMessage["type"]

export const isEmbeddedChatMessage = <Type extends EmbeddedChatMessageType>(
  data: unknown,
  type: Type
): data is Extract<EmbeddedChatMessage, { type: Type }> =>
  typeof data === "object" && data !== null && (data as { type?: unknown }).type === type
